import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, UserPlus, Mail, Phone, Shield, MessageCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { IdCard } from "lucide-react";
import EmployeeSheet from "@/components/app/EmployeeSheet";
import Avatar from "@/components/app/Avatar";
import { useAuth } from "@/contexts/AuthContext";

const emptyEmp = { email: "", name: "", password: "", phone: "", role: "dipendente", department: "operativo", hourly_rate: 0 };
const APP_URL = (typeof window !== "undefined") ? window.location.origin : "";

function normalizePhoneForWa(p) {
  if (!p) return "";
  let s = String(p).replace(/\s|-/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);
  if (!s.startsWith("+")) {
    if (/^3\d{8,9}$/.test(s)) s = "+39" + s;
  }
  return s.replace(/^\+/, "");
}

function buildWelcomeText(emp) {
  const loginId = emp.phone ? emp.phone : emp.email;
  const lines = [
    `Ciao ${emp.name}! Benvenuto in In & Out Traslochi 🚚`,
    "",
    "Ecco le tue credenziali per accedere all'app:",
    `👤 Login: ${loginId}`,
    `🔑 Password: ${emp.password}`,
    "",
    `🔗 Apri l'app: ${APP_URL}/login`,
    "",
    "👉 Dopo il primo accesso tocca 'Attiva notifiche' in alto a sinistra per ricevere i turni in tempo reale.",
  ];
  return lines.join("\n");
}

function buildWhatsAppLink(phone, text) {
  const digits = normalizePhoneForWa(phone);
  if (!digits || digits.length < 10) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

export default function Employees() {
  const { user: me } = useAuth();
  const owner = !me?.office;
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEmp);
  const [confirmDel, setConfirmDel] = useState(null);
  const [welcome, setWelcome] = useState(null); // {name, phone, email, password, waLink, text}
  const [copied, setCopied] = useState(false);
  const [sheet, setSheet] = useState(null);

  const load = () => api.get("/employees").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyEmp);
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({ email: e.email, name: e.name, phone: e.phone || "", role: e.role, department: e.department || "operativo", hourly_rate: e.hourly_rate || 0, password: "" });
    setOpen(true);
  };

  const save = async (ev) => {
    ev.preventDefault();
    try {
      if (editing) {
        const update = { name: form.name, phone: form.phone, role: form.role, department: form.department, hourly_rate: Number(form.hourly_rate) || 0 };
        if (form.password) update.password = form.password;
        await api.put(`/employees/${editing.id}`, update);
        toast.success("Dipendente aggiornato");
      } else {
        await api.post("/employees", form);
        toast.success("Dipendente creato");
        // Prepare WhatsApp welcome dialog with credentials
        const text = buildWelcomeText(form);
        const waLink = form.phone ? buildWhatsAppLink(form.phone, text) : null;
        setWelcome({ ...form, text, waLink });
        setCopied(false);
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/employees/${confirmDel.id}`);
      toast.success("Dipendente eliminato");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Team</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Dipendenti</h1>
          <p className="text-slate-500 mt-2 text-sm">Gestisci gli account dei dipendenti e dei responsabili.</p>
        </div>
        {owner && <Button onClick={openCreate} className="bg-sky-600 hover:bg-sky-700 text-white h-11" data-testid="create-employee-button">
          <UserPlus className="w-4 h-4 mr-2" /> Nuovo Dipendente
        </Button>}
      </header>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="employees-list">
        <div className="hidden md:grid grid-cols-12 bg-slate-50 text-[11px] uppercase tracking-widest font-bold text-slate-500 px-6 py-3">
          <div className="col-span-4">Nome</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Telefono</div>
          <div className="col-span-2">Ruolo</div>
          <div className="col-span-1 text-right">Azioni</div>
        </div>
        {list.length === 0 ? (
          <div className="p-10 text-center text-slate-400">Nessun dipendente ancora.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((e) => (
              <li key={e.id} className="px-4 md:px-6 py-4 md:grid md:grid-cols-12 md:gap-4 md:items-center" data-testid={`employee-row-${e.id}`}>
                <div className="col-span-4 flex items-center gap-3">
                  <Avatar user={e} />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{e.name}</div>
                    {e.active === false && <div className="text-[10px] text-rose-600 font-bold uppercase tracking-wider">Disattivato</div>}
                  </div>
                </div>
                <div className="col-span-3 text-sm text-slate-700 truncate flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" /> {e.email}
                </div>
                <div className="col-span-2 text-sm text-slate-700 font-mono flex items-center gap-1.5">
                  {e.phone ? <><Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />{e.phone}</> : <span className="text-slate-300">—</span>}
                </div>
                <div className="col-span-2">
                  <span
                    data-testid="employee-role-badge"
                    className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                      e.role === "admin"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-sky-50 text-sky-700 border-sky-200"
                    }`}
                  >
                    {e.role === "admin" && <Shield className="w-3 h-3" />}
                    {e.role === "admin" ? "Admin" : e.role === "ufficio" || e.department === "ufficio" ? "Ufficio" : "Operativo"}
                  </span>
                </div>
                <div className="col-span-1 flex justify-end gap-1 mt-3 md:mt-0">
                  <button
                    onClick={() => setSheet(e)}
                    title="Scheda e valutazioni"
                    className="p-2 rounded-lg hover:bg-amber-50 text-slate-500 hover:text-amber-600"
                    data-testid={`sheet-employee-${e.id}`}
                  >
                    <IdCard className="w-4 h-4" />
                  </button>
                  {owner && <button
                    onClick={() => openEdit(e)}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                    data-testid={`edit-employee-${e.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>}
                  {owner && <button
                    onClick={() => setConfirmDel(e)}
                    className="p-2 rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-600"
                    data-testid={`delete-employee-${e.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">
              {editing ? "Modifica Dipendente" : "Nuovo Dipendente"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="employee-form">
            <div>
              <Label>Nome e cognome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="employee-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!editing} data-testid="employee-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="employee-phone-input" />
              </div>
              <div>
                <Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="employee-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dipendente">Dipendente</SelectItem>
                    <SelectItem value="ufficio">Ufficio (vede il gestionale, non paghe e impostazioni)</SelectItem>
                    <SelectItem value="admin">Amministratore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.role === "dipendente" && (
              <div>
                <Label>Reparto (per la formazione)</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger data-testid="employee-department-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operativo">Squadra operativa</SelectItem>
                    <SelectItem value="ufficio">Ufficio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.role === "dipendente" && (
              <div>
                <Label>Tariffa oraria (€)</Label>
                <Input type="number" min="0" step="0.5" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="employee-hourly-rate-input" />
              </div>
            )}
            <div>
              <Label>{editing ? "Nuova password (lascia vuoto per non cambiare)" : "Password"}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!editing}
                data-testid="employee-password-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="save-employee-button">
                {editing ? "Salva" : "Crea"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Welcome WhatsApp dialog shown right after employee creation */}
      <Dialog open={!!welcome} onOpenChange={(o) => !o && setWelcome(null)}>
        <DialogContent data-testid="welcome-whatsapp-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-emerald-600" /> Invia credenziali via WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              <b>{welcome?.name}</b> è stato creato. Mandagli le credenziali e il link all'app con un clic.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-mono whitespace-pre-wrap text-slate-700 max-h-56 overflow-y-auto">
              {welcome?.text}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {welcome?.waLink ? (
                <a
                  href={welcome.waLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="send-whatsapp-welcome"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2.5 rounded-lg flex-1 transition"
                >
                  <MessageCircle className="w-4 h-4" /> Invia su WhatsApp
                </a>
              ) : (
                <div className="flex-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  ⚠️ Numero di telefono mancante o non valido — non posso generare il link WhatsApp. Comunica le credenziali a voce.
                </div>
              )}
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(welcome.text);
                    setCopied(true);
                    toast.success("Testo copiato");
                    setTimeout(() => setCopied(false), 2000);
                  } catch {
                    toast.error("Impossibile copiare");
                  }
                }}
                className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2.5 rounded-lg transition"
                data-testid="copy-welcome-button"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copiato" : "Copia"}
              </button>
            </div>
            <div className="flex justify-end pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => setWelcome(null)} data-testid="close-welcome-dialog">
                Chiudi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EmployeeSheet employee={sheet} onClose={() => { setSheet(null); load(); }} />

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {confirmDel?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              L'account verrà eliminato definitivamente e rimosso da tutti i lavori assegnati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="confirm-delete-employee">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
